import { useMemo, useState } from "react";
import useSeo from "@/hooks/useSeo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import CompareCard from "@/components/feature/CompareCard";
import Container from "@/components/base/Container";
import { compareItems } from "@/mocks/compareData";
import { categoryFilters } from "@/mocks/site";

export default function CompareListPage() {
  const [active, setActive] = useState("all");

  const filtered = useMemo(() => {
    if (active === "all") return compareItems;
    return compareItems.filter((item) => item.category === active);
  }, [active]);

  useSeo({
    title: "网红明星对比榜单 | 同框与咖位差距全收录-咖位观察局",
    description:
      "按维度收录网红明星跨界对比合集，覆盖直播同框、品牌代言、综艺同台、时尚红毯与影视选角，用热度、话题、商业、专业四个维度看清咖位差距与谁更火。",
    keywords: "网红明星对比榜单,咖位对比,跨界对比,同框名场面,网红明星谁更火,咖位差距",
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          name: "网红明星对比榜单",
          description: "收录网红明星跨界对比合集的集合页，可按维度筛选。",
          inLanguage: "zh-CN",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "/" },
            { "@type": "ListItem", position: 2, name: "对比榜单" },
          ],
        },
        {
          "@type": "ItemList",
          itemListElement: compareItems.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.title,
          })),
        },
      ],
    },
  });

  return (
    <>
      <SiteHeader />
      <main className="pt-16 md:pt-20">
        <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "对比榜单" }]} />

        <section className="border-b border-background-200/80 bg-background-100 py-12 md:py-16">
          <Container>
            <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-primary-600">
              <span className="h-px w-6 bg-primary-500" />
              COMPARE LIBRARY
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
              网红明星对比榜单
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              这里收录了我们在直播同框、品牌代言、综艺同台、时尚红毯与影视选角五个场域里的对比合集。每一组都给出可展开的维度打分，你可以按关心的场景筛选查看。
            </p>

            <div className="mt-8 flex flex-wrap gap-2 rounded-lg bg-background-50 p-1.5">
              {categoryFilters.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setActive(filter.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    active === filter.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </Container>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <Container>
            <div className="mb-6 flex items-center justify-between text-sm text-foreground-500">
              <span>
                共 <strong className="text-foreground-900">{filtered.length}</strong> 组对比
              </span>
              {active !== "all" ? (
                <button
                  type="button"
                  onClick={() => setActive("all")}
                  className="inline-flex items-center gap-1 whitespace-nowrap text-primary-600 transition-colors hover:text-primary-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-close-line text-base" />
                  </span>
                  清除筛选
                </button>
              ) : null}
            </div>

            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filtered.map((item) => (
                  <CompareCard
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    category={item.category}
                    summary={item.summary}
                    coverImage={item.coverImage}
                    leftName={item.leftName}
                    rightName={item.rightName}
                    heat={item.heat}
                    date={item.date}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-background-200 bg-background-100 px-6 py-16 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-line text-2xl" />
                </span>
                <p className="mt-4 text-sm text-foreground-600">该场景下暂时还没有对比内容，换个维度看看。</p>
              </div>
            )}
          </Container>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}